import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Resolve image URLs - normalizes any /api/files/ URL to use the current backend
export function resolveImageUrl(url) {
    if (!url) return '';
    // Extract /api/files/ path from any domain and route through current backend
    const filesMatch = url.match(/\/api\/files\/(.*)/);
    if (filesMatch) return `${BACKEND_URL}/api/files/${filesMatch[1]}`;
    // Already a full external URL (e.g. ics-dryice.com)
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    // Relative path
    if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
    return url;
}

// Public API client
const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Admin API client with basic auth
const createAdminApi = (username, password) => {
    return axios.create({
        baseURL: API_BASE,
        headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Basic ' + btoa(`${username}:${password}`),
        },
    });
};

// Public endpoints
export const publicApi = {
    // Categories
    getCategories: () => api.get('/categories'),
    getCategory: (slug) => api.get(`/categories/${slug}`),
    
    // Products
    getProducts: (params = {}) => api.get('/products', { params }),
    getProduct: (id) => api.get(`/products/${id}`),
    getFeaturedProducts: () => api.get('/products', { params: { featured: true } }),
    
    // Quote requests
    submitQuote: (data) => api.post('/quotes', data),
    
    // Comparison tables
    getComparisons: () => api.get('/comparisons'),
    getComparison: (id) => api.get(`/comparisons/${id}`),
    
    // Site settings
    getSettings: () => api.get('/settings'),
    
    // Orders
    createOrder: (data) => api.post('/orders', data),
    
    // Rentals
    getRentalMachines: () => api.get('/rentals/machines'),
    getRentalMachine: (id) => api.get(`/rentals/machines/${id}`),
    getRentalAvailability: (id) => api.get(`/rentals/machines/${id}/availability`),
    getRentalQuote: (data) => api.post('/rentals/quote', data),
    createBooking: (data) => api.post('/rentals/book', data),
    getBooking: (id) => api.get(`/rentals/booking/${id}`),
    getBookingStatus: (id) => api.get(`/rentals/booking/${id}/status`),
    getStripeConfig: () => api.get('/stripe/config'),
    
    // Blog
    getBlogPosts: (params = {}) => api.get('/blog', { params }),
    getBlogPost: (slug) => api.get(`/blog/${slug}`),
    getBlogTags: () => api.get('/blog/tags'),
};

// Admin endpoints factory
export const createAdminApiClient = (username, password) => {
    const adminApi = createAdminApi(username, password);
    
    return {
        // Auth
        login: () => adminApi.post('/admin/login'),
        
        // Stats
        getStats: () => adminApi.get('/admin/stats'),
        
        // Categories
        getCategories: () => adminApi.get('/admin/categories'),
        createCategory: (data) => adminApi.post('/admin/categories', data),
        updateCategory: (id, data) => adminApi.put(`/admin/categories/${id}`, data),
        deleteCategory: (id) => adminApi.delete(`/admin/categories/${id}`),
        
        // Products
        getProducts: () => adminApi.get('/admin/products'),
        createProduct: (data) => adminApi.post('/admin/products', data),
        updateProduct: (id, data) => adminApi.put(`/admin/products/${id}`, data),
        deleteProduct: (id) => adminApi.delete(`/admin/products/${id}`),
        
        // Quote requests
        getQuotes: (status) => adminApi.get('/admin/quotes', { params: { status } }),
        updateQuote: (id, status, notes) => adminApi.put(`/admin/quotes/${id}`, null, { params: { status, notes } }),
        deleteQuote: (id) => adminApi.delete(`/admin/quotes/${id}`),
        
        // Comparison tables
        getComparisons: () => adminApi.get('/admin/comparisons'),
        createComparison: (data) => adminApi.post('/admin/comparisons', data),
        updateComparison: (id, data) => adminApi.put(`/admin/comparisons/${id}`, data),
        deleteComparison: (id) => adminApi.delete(`/admin/comparisons/${id}`),
        
        // Settings
        getSettings: () => adminApi.get('/admin/settings'),
        updateSettings: (data) => adminApi.put('/admin/settings', data),
        
        // Orders
        getOrders: () => adminApi.get('/admin/orders'),
        updateOrder: (id, data) => adminApi.put(`/admin/orders/${id}`, data),
        
        // Blog
        getBlogPosts: () => adminApi.get('/admin/blog'),
        createBlogPost: (data) => adminApi.post('/admin/blog', data),
        updateBlogPost: (id, data) => adminApi.put(`/admin/blog/${id}`, data),
        deleteBlogPost: (id) => adminApi.delete(`/admin/blog/${id}`),
    };
};

export default api;
